'use client';

import { memo, useEffect, useState, useRef, useCallback } from 'react';
import { Mic, X, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Props for the RecordingOverlay component
 */
export interface RecordingOverlayProps {
  /**
   * Whether recording is active
   */
  isRecording: boolean;
  /**
   * Elapsed recording time in seconds
   */
  elapsedTime: number;
  /**
   * Callback when cancel button is clicked
   */
  onCancel: () => void;
  /**
   * Callback when confirm button is clicked
   */
  onConfirm: () => void;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether the component is in compact mode
   */
  isCompact?: boolean;
  /**
   * Whether transcription is in progress
   */
  isTranscribing?: boolean;
}

/**
 * Format seconds to M:SS format (like "0:04")
 */
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Audio waveform with voice detection
 * - Shows primary color waveform when voice is detected
 * - Shows gray waveform when silent
 * - Bars are rounded and thin
 * - Flows continuously from right to left
 * - Uses flex to distribute bars evenly across full width
 * - Pauses animation when transcribing
 */
const AudioWaveform = memo(({
  isRecording,
  isTranscribing,
}: {
  isRecording: boolean;
  isTranscribing?: boolean;
}) => {
  const [bars, setBars] = useState<Array<{ level: number; isVoice: boolean }>>([]);
  const animationRef = useRef<number | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const lastUpdateRef = useRef(0);

  useEffect(() => {
    // When transcribing, pause animation but keep bars frozen
    if (isTranscribing) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      // Cleanup audio but keep the bars visible
      if (analyserRef.current) {
        const analyser = analyserRef.current;
        if (analyser['_source']) analyser['_source'].disconnect();
        if (analyser['_context']) analyser['_context'].close();
        if (analyser['_stream']) analyser['_stream'].getTracks().forEach((track: MediaStreamTrack) => track.stop());
        analyserRef.current = null;
      }
      return;
    }

    // When not recording at all, clear everything
    if (!isRecording) {
      setBars([]);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      // Cleanup audio
      if (analyserRef.current) {
        const analyser = analyserRef.current;
        if (analyser['_source']) analyser['_source'].disconnect();
        if (analyser['_context']) analyser['_context'].close();
        if (analyser['_stream']) analyser['_stream'].getTracks().forEach((track: MediaStreamTrack) => track.stop());
        analyserRef.current = null;
      }
      return;
    }

    // Setup audio analysis for voice detection
    const setupAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const audioContext = new AudioContext({ sampleRate: 44100 });
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.3;
        analyserRef.current = analyser;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        // Store refs for cleanup
        analyserRef.current['_stream'] = stream;
        analyserRef.current['_context'] = audioContext;
        analyserRef.current['_source'] = source;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        // Animation loop
        const animate = () => {
          if (!analyserRef.current) return;

          analyserRef.current.getByteFrequencyData(dataArray);

          // Calculate average level for voice detection
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
          }
          const avgLevel = sum / dataArray.length;

          // Voice detection threshold
          const isVoice = avgLevel > 10;

          // Update bars for flowing animation
          const now = Date.now();
          if (now - lastUpdateRef.current > 25) {
            lastUpdateRef.current = now;

            setBars((prevBars) => {
              const newBars = [...prevBars];

              // Remove first bar (leftmost) - creates leftward flow
              if (newBars.length >= 150) {
                newBars.shift();
              }

              // Add new bar at the end (rightmost)
              if (isVoice) {
                // Voice detected - primary color bar with dynamic height
                const level = Math.min(1, (avgLevel / 60) * 1.8);
                newBars.push({ level, isVoice: true });
              } else {
                // Silent - small static gray bar
                newBars.push({ level: 0.15, isVoice: false });
              }

              return newBars;
            });
          }

          animationRef.current = requestAnimationFrame(animate);
        };

        // Initialize with small static gray bars
        const initialBars: Array<{ level: number; isVoice: boolean }> = [];
        for (let i = 0; i < 150; i++) {
          initialBars.push({ level: 0.15, isVoice: false });
        }
        setBars(initialBars);

        animate();
      } catch (error) {
        console.error('Audio analysis failed:', error);
        // Fallback: show flowing animation
        const fallbackAnimate = () => {
          const now = Date.now();
          if (now - lastUpdateRef.current > 25) {
            lastUpdateRef.current = now;
            setBars((prevBars) => {
              const newBars = [...prevBars];
              if (newBars.length >= 150) {
                newBars.shift();
              }
              newBars.push({ level: 0.15, isVoice: false });
              return newBars;
            });
          }
          animationRef.current = requestAnimationFrame(fallbackAnimate);
        };

        const initialBars: Array<{ level: number; isVoice: boolean }> = [];
        for (let i = 0; i < 150; i++) {
          initialBars.push({ level: 0.15, isVoice: false });
        }
        setBars(initialBars);
        fallbackAnimate();
      }
    };

    setupAudio();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (analyserRef.current) {
        const analyser = analyserRef.current;
        if (analyser['_source']) analyser['_source'].disconnect();
        if (analyser['_context']) analyser['_context'].close();
        if (analyser['_stream']) analyser['_stream'].getTracks().forEach((track: MediaStreamTrack) => track.stop());
        analyserRef.current = null;
      }
    };
  }, [isRecording, isTranscribing]);

  // Determine bar color based on voice detection
  const getBarColor = (bar: { level: number; isVoice: boolean }) => {
    // Use isVoice flag to determine color
    return bar.isVoice
      ? 'bg-primary' // Primary color for voice
      : 'bg-gray-200 dark:bg-gray-700'; // Lighter gray for silent
  };

  return (
    <div className="flex items-center h-full w-full gap-[2px]">
      {bars.map((bar, i) => (
        <div
          key={i}
          className={cn(
            'flex-1 rounded-full',
            getBarColor(bar)
          )}
          style={{
            height: `${8 + bar.level * 28}px`,
            maxHeight: '40px',
            transition: 'none',
          }}
        />
      ))}
    </div>
  );
});

AudioWaveform.displayName = 'AudioWaveform';

/**
 * Voice recording overlay - replaces textarea during recording
 * Shows full-width waveform with timer and action buttons on same line
 */
export const RecordingOverlay = memo(({
  isRecording,
  elapsedTime,
  onCancel,
  onConfirm,
  className,
  isCompact = false,
  isTranscribing = false,
}: RecordingOverlayProps) => {
  const handleCancel = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onCancel();
  }, [onCancel]);

  const handleConfirm = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onConfirm();
  }, [onConfirm]);

  return (
    <div
      className={cn(
        'w-full bg-transparent outline-none flex items-center justify-between',
        isCompact ? 'p-1.5 text-sm min-h-[32px]' : 'p-2 min-h-[44px]'
      )}
    >
      {/* Waveform takes available space */}
      <div className="flex-1 flex items-center overflow-hidden">
        <AudioWaveform isRecording={isRecording} isTranscribing={isTranscribing} />
      </div>

      {/* Timer, mic icon and action buttons on the right */}
      <div className="flex items-center gap-2 flex-shrink-0 pl-2">
        {/* Mic icon */}
        <div className="flex items-center justify-center rounded-full bg-primary/10 h-6 w-6">
          <Mic className="h-3 w-3 text-primary" />
        </div>

        {/* Timer */}
        <span className="text-sm text-foreground tabular-nums min-w-[3ch]">
          {formatTime(elapsedTime)}
        </span>

        {/* Cancel button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex items-center justify-center',
                'hover:bg-muted transition-colors',
                'h-8 w-8 rounded-full'
              )}
              onClick={handleCancel}
            >
              <X className="size-5 text-foreground" strokeWidth={2} />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Cancel recording</p>
          </TooltipContent>
        </Tooltip>

        {/* Send button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex items-center justify-center',
                'hover:bg-muted transition-colors',
                'h-8 w-8 rounded-full'
              )}
              onClick={handleConfirm}
              disabled={isTranscribing}
            >
              {isTranscribing ? (
                <Loader2 className="size-5 text-foreground animate-spin" />
              ) : (
                <Check className="size-5 text-foreground" strokeWidth={2.5} />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isTranscribing ? 'Transcribing...' : 'Send recording'}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
});

RecordingOverlay.displayName = 'RecordingOverlay';
